import { formatCurrency, formatProductPrice, getOmnibusLowestPrice, resolvePreviousPrice } from '../../../shared/utils/priceUtils.js'

/**
 * buildPricingData — derives all display-ready pricing fields for PricingPane.
 *
 * Pure function: receives product + display options, returns a shape ready for
 * direct rendering. Contains no React state or hooks.
 *
 * Adding a new field:
 *  1. Compute from product data here.
 *  2. Add to the returned shape.
 *  3. Use in PricingPane directly.
 *
 * @param {object}  product
 * @param {object}  [options]
 * @param {boolean} [options.pricesVisible=true]
 * @param {boolean} [options.pricesSoldVisible=true]
 * @param {boolean} [options.pricesReservedVisible=true]
 * @param {string}  [options.currency='PLN']   ISO 4217 currency code
 * @param {string}  [options.locale='pl-PL']   IETF locale tag
 * @returns {{
 *   isHidden: boolean,
 *   isOnOffer: boolean,
 *   pricePerSqm: string|null,
 *   totalPrice: string,
 *   strikethroughPrice: string|null,
 *   omnibusPrice: string|null,
 *   vatNote: string|null,
 *   resolvedPrice: number|null,
 *   currency: string,
 *   locale: string,
 * }}
 */
export function buildPricingData(product, {
  pricesVisible = true,
  pricesSoldVisible = true,
  pricesReservedVisible = true,
  currency = 'PLN',
  locale = 'pl-PL',
} = {}) {
  const { resolvedPrice, isHidden, isOnOffer, displayPrice, strikethroughPrice } = formatProductPrice(
    product,
    { pricesVisible, pricesSoldVisible, pricesReservedVisible, currency, locale },
  )

  const vatNote = product?.price_vat ? `${product.price_vat}%` : null

  const area = product?.area_original ?? null

  let pricePerSqm = null
  let strikethroughPricePerSqm = null
  if (!isHidden && resolvedPrice != null && area) {
    pricePerSqm = formatCurrency(resolvedPrice / area, { currency, locale })
    if (isOnOffer) {
      const prevTotal = resolvePreviousPrice({product})
      if (prevTotal != null) {
        strikethroughPricePerSqm = formatCurrency(prevTotal / area, { currency, locale })
      }
    }
  }

  let omnibusPrice = null
  let omnibusPricePerSqm = null
  if (isOnOffer && !isHidden) {
    const history = product?.price_history ?? []
    const lowest = getOmnibusLowestPrice(history, { sqm: false })
    if (lowest != null) {
      omnibusPrice = formatCurrency(lowest, { currency, locale })
    }
    if (area) {
      const lowestSqm = getOmnibusLowestPrice(history, { sqm: true, area })
      if (lowestSqm != null) {
        omnibusPricePerSqm = formatCurrency(lowestSqm, { currency, locale })
      }
    }
  }

  return {
    isHidden,
    isOnOffer,
    pricePerSqm,
    strikethroughPricePerSqm,
    omnibusPricePerSqm,
    totalPrice: displayPrice,
    strikethroughPrice,
    omnibusPrice,
    vatNote,
    resolvedPrice,
    currency,
    locale,
  }
}
